import { ArrowDown, ArrowUp, FolderPlus, LayoutGrid, List, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import type { SortDirection, SortField } from "./files-types";

const SORT_LABELS: Record<SortField, string> = {
  name: "Nome",
  date: "Data",
  size: "Tamanho",
  type: "Tipo",
};

export function FilesToolbar({
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  sortField,
  onSortFieldChange,
  sortDirection,
  onToggleSortDirection,
  onNewFolder,
  uploadSlot,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  viewMode: "grade" | "lista";
  onViewModeChange: (v: "grade" | "lista") => void;
  sortField: SortField;
  onSortFieldChange: (v: SortField) => void;
  sortDirection: SortDirection;
  onToggleSortDirection: () => void;
  onNewFolder: () => void;
  /** Botão de anexar arquivo — renderizado ao lado de "Nova pasta". Slot em vez de props próprias
   *  pra não acoplar esse toolbar genérico à lógica de upload. */
  uploadSlot?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="relative min-w-[200px] flex-1 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar por nome..."
          className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
        />
      </div>

      <div className="flex items-center gap-1">
        <select
          value={sortField}
          onChange={(e) => onSortFieldChange(e.target.value as SortField)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
        >
          {(Object.keys(SORT_LABELS) as SortField[]).map((f) => (
            <option key={f} value={f}>
              Ordenar por {SORT_LABELS[f]}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onToggleSortDirection}
          title={sortDirection === "asc" ? "Ordem crescente" : "Ordem decrescente"}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
        >
          {sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
        </button>
      </div>

      <button
        type="button"
        data-tour="arquivos-nova-pasta"
        onClick={onNewFolder}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
      >
        <FolderPlus className="h-4 w-4" />
        Nova pasta
      </button>

      {uploadSlot}

      <div data-tour="arquivos-visualizacao" className="ml-auto inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
        <button
          type="button"
          onClick={() => onViewModeChange("grade")}
          aria-label="Visualizar em grade"
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded px-3 text-xs font-medium transition-colors",
            viewMode === "grade"
              ? "bg-brand-600 font-semibold text-white"
              : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
          )}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          Grade
        </button>
        <button
          type="button"
          onClick={() => onViewModeChange("lista")}
          aria-label="Visualizar em lista"
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded px-3 text-xs font-medium transition-colors",
            viewMode === "lista"
              ? "bg-brand-600 font-semibold text-white"
              : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
          )}
        >
          <List className="h-3.5 w-3.5" />
          Lista
        </button>
      </div>
    </div>
  );
}
